// Writing a call down.
//
// Everything worth keeping about an inspection except the video: what was said,
// what was extracted from it, what it was priced at, how long it took, which
// checklist was worked through, and who took the call.
//
// Written twice. The row is created the moment the call is joined, so that a
// call that happened is on record whatever becomes of the browser afterwards;
// it is filled in with the transcript, the answers and the duration when the
// inspector leaves.
//
// Creating it up front is the difference between "no record" and "a record with
// no transcript" for every call that does not end tidily - a closed tab, a
// phone that went to sleep, a crash. Those are common, and a call nobody can
// find is much worse than a thin one.
//
// Not saved continuously in between: the pipeline recomputes the whole answer
// every seven seconds, and persisting that would write the same record hundreds
// of times per call to capture a state only meaningful once it settles.

import { endpoints } from '../config/endpoints';
import { getToken } from '../auth/session';
import type { ChecklistConfig } from '../config/types';
import type { FieldOverride, FieldState } from '../pipeline/extraction';
import { mergedFields } from '../pipeline/extraction';
import { estimateCents } from '../pipeline/estimate';
import type { TranscriptSegment } from '../pipeline/Transcriber';

export type SessionToSave = {
  callId: string;
  startedAt: Date | null;
  endedAt: Date;
  durationSeconds: number;
  config: ChecklistConfig | null;
  state: FieldState[];
  overrides: Map<string, FieldOverride>;
  segments: TranscriptSegment[];
};

type Posted = { problem: string | null };

/** Shared by both writes. `keepalive` lets one outlive the page that sent it. */
async function post(body: unknown, keepalive = false): Promise<Posted> {
  if (!endpoints.inspections) {
    return { problem: 'This build has no previous-calls endpoint, so the call was not saved.' };
  }
  const token = getToken();
  if (!token) {
    return { problem: 'Your session has expired, so the call was not saved.' };
  }

  let response: Response;
  try {
    response = await fetch(`${endpoints.inspections.replace(/\/+$/, '')}/`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-api-key': token },
      body: JSON.stringify(body),
      keepalive,
    });
  } catch {
    return { problem: 'Could not reach the previous-calls service, so the call was not saved.' };
  }

  if (!response.ok) {
    let detail = `The service returned ${response.status}.`;
    try {
      const parsed = (await response.json()) as { error?: unknown };
      if (typeof parsed.error === 'string') detail = parsed.error;
    } catch {
      /* the status is enough */
    }
    return { problem: `The call was not saved. ${detail}` };
  }

  return { problem: null };
}

/**
 * Put the call on record as soon as it is joined.
 *
 * Deliberately sends no fields and no transcript - not empty ones. The endpoint
 * only replaces what it is given, so omitting them means a later save fills
 * them in, and a retry of this one can never wipe them.
 */
export function startSession(
  callId: string,
  startedAt: Date,
  config: ChecklistConfig | null,
): Promise<Posted> {
  return post({
    callId,
    startedAt: startedAt.toISOString(),
    checklistId: config?.id ?? null,
    checklistName: config?.name ?? null,
  });
}

/**
 * How a value got its answer, in the vocabulary the record uses.
 *
 * The app tracks 'confirmed' (a person agreed with the model) and 'typed' (a
 * person wrote it themselves); anything with no override is the model's own
 * work. Kept distinct because "the model said it" and "somebody checked it" are
 * very different things to read six months later.
 */
function sourceOf(override: FieldOverride | undefined): string {
  if (!override) return 'extracted';
  return override.source === 'typed' ? 'manual' : 'confirmed';
}

export function saveSession(session: SessionToSave, keepalive = false): Promise<Posted> {
  const entries = mergedFields(session.config?.fields ?? [], session.state, session.overrides);

  const body = {
    callId: session.callId,
    startedAt: session.startedAt?.toISOString() ?? null,
    endedAt: session.endedAt.toISOString(),
    durationSeconds: session.durationSeconds,
    checklistId: session.config?.id ?? null,
    // Sent alongside the id so the record still says what was worked through
    // after the checklist is renamed or deleted.
    checklistName: session.config?.name ?? null,
    // Null unless every field was answered - the same rule the panel uses to
    // decide whether to show a price at all.
    estimatedPriceCents: estimateCents(entries, session.config),
    fields: entries.map((entry) => ({
      fieldKey: entry.id,
      label: entry.label,
      // Empty stays empty rather than becoming "", because a field the call
      // never answered is a fact about the call worth keeping.
      value: entry.value,
      source: sourceOf(session.overrides.get(entry.id)),
    })),
    // No speaker: both sides are mixed into one stream before transcription, so
    // there is genuinely nothing to attribute. The column allows null rather
    // than this inventing one.
    transcript: session.segments.map((segment) => ({
      atMs: segment.startMs,
      text: segment.text,
    })),
  };

  return post(body, keepalive);
}
